import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuthContext } from '../App';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

function MyTenantDetail() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [tenant, setTenant] = useState(null);
  const [leases, setLeases] = useState([]);
  const [leaseFile, setLeaseFile] = useState(null);
  const [leaseUploading, setLeaseUploading] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [endingTenancy, setEndingTenancy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  // Renewal state
  const [renewal, setRenewal] = useState(null);
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [showRenewalForm, setShowRenewalForm] = useState(false);
  const [renewalForm, setRenewalForm] = useState({ proposed_rent: '', proposed_start_date: '', proposed_end_date: '', note: '' });
  const [renewalMessage, setRenewalMessage] = useState('');
  // chat state removed
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTenant = async () => {
      if (!user || !user.token) {
        setError('Please log in to view tenant details.');
        setLoading(false);
        return;
      }

      try {
        const [tenantRes, leaseRes, renewalRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/landlords/active-tenants/${id}`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${API_BASE_URL}/api/landlords/active-tenants/${id}/leases`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${API_BASE_URL}/api/landlords/active-tenants/${id}/renewal`, {
            headers: { Authorization: `Bearer ${user.token}` }
          })
        ]);

        if (!tenantRes.ok) {
          throw new Error('Failed to fetch tenant details.');
        }

        const tenantData = await tenantRes.json();
        const leaseData = leaseRes.ok ? await leaseRes.json() : { leases: [] };
        const renewalData = renewalRes.ok ? await renewalRes.json().catch(() => ({})) : {};
        setTenant(tenantData);
        setLeases(leaseData.leases || []);
        setRenewal(renewalData.renewal || null);
        if (tenantData?.monthly_rent) {
          setRenewalForm(prev => ({ ...prev, proposed_rent: prev.proposed_rent || tenantData.monthly_rent }));
        }
      } catch (err) {
        setError(err.message || 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [user, id]);

  useEffect(() => {
    if (!tenant?.tenant_id || !user?.token) return;
    let isActive = true;
    const fetchPayments = async () => {
      setPaymentsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/landlords/payments?tenant_id=${tenant.tenant_id}&status=succeeded`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load payments');
        const payments = data.payments || [];
        const latest = payments.sort((a, b) => {
          const aTime = new Date(a.paid_at || a.created_at || a.due_date || 0).getTime();
          const bTime = new Date(b.paid_at || b.created_at || b.due_date || 0).getTime();
          return bTime - aTime;
        })[0];
        if (isActive) setLastPayment(latest || null);
      } catch (err) {
        if (isActive) setLastPayment(null);
      } finally {
        if (isActive) setPaymentsLoading(false);
      }
    };
    fetchPayments();
    return () => {
      isActive = false;
    };
  }, [tenant?.tenant_id, user?.token]);

  useEffect(() => {
    const fetchChat = async () => {
      if (!user || !user.token) return;
      if (!tenant?.application_id) return;
      setChatLoading(true);
      setChatError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/applications/${tenant.application_id}/comments`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (!res.ok) throw new Error('Failed to load chat.');
        const data = await res.json();
        setChatMessages(data.comments || []);
      } catch (err) {
        setChatError(err.message || 'Could not load chat.');
      } finally {
        setChatLoading(false);
      }
    };

    fetchChat();
  }, [user, tenant?.application_id]);

  if (loading) {
    return (
      <main className="App-main tenants-page">
        <p className="status-text">Loading tenant details...</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="App-main tenants-page">
        <p className="status-text error-text">{error}</p>
        <Link className="ghost-button" to="/landlord/my-tenants">Back to Tenants</Link>
      </main>
    );
  }

  const handleLeaseUpload = async () => {
    if (!leaseFile) return;
    setLeaseUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('lease', leaseFile);
      const res = await fetch(`${API_BASE_URL}/api/landlords/active-tenants/${id}/lease`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to upload lease.');
      }
      if (data.lease) {
        setLeases(prev => [data.lease, ...prev]);
      }
      setLeaseFile(null);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLeaseUploading(false);
    }
  };

  const handleEndTenancy = async () => {
    if (!tenant || !user?.token) return;
    if (!window.confirm('End this tenancy and stop future rent charges?')) return;
    setEndingTenancy(true);
    setActionMessage('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/landlords/active-tenants/${tenant.id}/end-tenancy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to end tenancy.');
      }
      setTenant(prev => prev ? { ...prev, status: 'ended', autopay_enabled: false } : prev);
      setActionMessage(`Tenancy ended. ${data.cancelled_ledgers || 0} future ledger(s) cancelled.`);
    } catch (err) {
      setActionMessage(err.message || 'Failed to end tenancy.');
    } finally {
      setEndingTenancy(false);
    }
  };

  const handleLeaseDownload = (leaseItem) => {
    if (!leaseItem?.id) return;
    window.open(`${API_BASE_URL}/api/landlords/leases/${leaseItem.id}/download`, '_blank');
  };

  const handleLeaseDelete = async (leaseItem) => {
    if (!leaseItem?.id) return;
    if (!window.confirm('Delete this lease document?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/landlords/leases/${leaseItem.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete lease.');
      setLeases(prev => prev.filter(item => item.id !== leaseItem.id));
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    }
  };

  const handleSendChat = async () => {
    if (!chatText.trim()) return;
    if (!user || !user.token) return;
    setChatSending(true);
    setChatError('');
    try {
      if (!tenant?.application_id) return;
      const res = await fetch(`${API_BASE_URL}/api/applications/${tenant.application_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ comment_text: chatText.trim(), origin: 'lease' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send message.');
      setChatMessages((prev) => [...prev, data.comment]);
      setChatText('');
    } catch (err) {
      setChatError(err.message || 'Could not send message.');
    } finally {
      setChatSending(false);
    }
  };



  if (!tenant) {
    return (
      <main className="App-main tenants-page">
        <p className="status-text">No tenant details found.</p>
        <Link className="ghost-button" to="/landlord/my-tenants">Back to Tenants</Link>
      </main>
    );
  }


  return (
    <main className="App-main tenants-page">
      <section className="ledger-header has-actions">
        <div>
          <p className="eyebrow-tag">Active lease</p>
          <h2>{tenant.tenant_name || tenant.full_name || 'Tenant'}</h2>
          <p className="tenant-property">{tenant.property_address || tenant.address || 'Property on file'}</p>
          {actionMessage && <p className="status-text" style={{ marginTop: '0.5rem' }}>{actionMessage}</p>}
        </div>
        <div className="ledger-actions tenant-detail-actions">
          <Link className="ghost-button" to="/landlord/my-tenants">Back to Tenants</Link>
        </div>
      </section>

      <section className="tenant-detail-grid">
        <article className="tenant-detail-card ledger-item">
          <h3>Lease Snapshot</h3>
          <div className="tenant-detail-section">
            <h4>Lease Overview</h4>
            <ul>
              <li><strong>Status:</strong> {tenant.status || 'active'}</li>
              <li><strong>Monthly Rent:</strong> ${tenant.monthly_rent || tenant.rent || '—'}</li>
              <li><strong>Rent Due Day:</strong> {tenant.rent_due_day || '—'}</li>
              <li><strong>Next Due Date:</strong> {tenant.next_due_date || tenant.rent_due_date || '—'}</li>
              <li><strong>Lease Start:</strong> {tenant.lease_start_date || '—'}</li>
              <li><strong>Lease End:</strong> {tenant.lease_end_date || '—'}</li>
            </ul>
          </div>

          <div className="tenant-detail-section">
            <h4>Leases</h4>
            {leases?.length > 0 ? (
              <ul className="document-list" style={{ marginBottom: '1rem' }}>
                {leases.map((leaseItem) => (
                  <li key={leaseItem.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="link-button" onClick={() => handleLeaseDownload(leaseItem)}>
                      {leaseItem.original_filename || 'Lease document'}
                    </button>
                    <span className="status-text">{leaseItem.status || 'sent'}</span>
                    <button type="button" className="ghost-button ghost-button--tiny" onClick={() => handleLeaseDelete(leaseItem)}>
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Status: <strong>not uploaded</strong></p>
            )}
            <div className="upload-controls" style={{ marginTop: '0.75rem' }}>
              <label className="file-button" htmlFor="lease-upload">
                Choose File
                <input id="lease-upload" type="file" onChange={(e) => setLeaseFile(e.target.files[0])} />
              </label>
              <span className="file-name">{leaseFile?.name || 'No file chosen'}</span>
              <button className="primary-button upload-button" onClick={handleLeaseUpload} disabled={leaseUploading || !leaseFile}>
                {leaseUploading ? 'Uploading...' : 'Upload Lease'}
              </button>
            </div>
          </div>

        </article>

        <article className="tenant-detail-card ledger-item">
          <h3>Rent Activity</h3>
          <p className="status-text">
            Last rent:{' '}
            {paymentsLoading ? 'Loading…' : lastPayment
              ? `${formatCurrency(lastPayment.amount || 0)} on ${new Date(lastPayment.paid_at || lastPayment.created_at || lastPayment.due_date).toLocaleDateString()}`
              : '—'}
          </p>
          <p className="status-text">
            Next rent: {tenant.monthly_rent ? formatCurrency(tenant.monthly_rent) : '—'} due {tenant.next_due_date || '—'}
          </p>
          <Link className="ghost-button" to="/landlord/ledger">View Ledger</Link>
        </article>

        <article className="tenant-detail-card ledger-item">
          <h3>Contact & Communication</h3>
          <ul>
            <li><strong>Email:</strong> {tenant.tenant_email || tenant.email || '—'}</li>
            <li><strong>Phone:</strong> {tenant.tenant_phone || tenant.phone_number || '—'}</li>
            <li><strong>Preferred Channel:</strong> {tenant.preferred_channel || 'Email'}</li>
          </ul>

          <div className="lease-chat">
            <div className="lease-chat-header">
              <h4>Lease Chat</h4>
              <span className="status-text">Active tenants on this lease</span>
            </div>
            {chatError && <p className="error-text">{chatError}</p>}
            <div className="lease-chat-thread">
              {chatLoading ? (
                <p className="status-text">Loading chat...</p>
              ) : chatMessages.length === 0 ? (
                <p className="status-text">No messages yet. Start the conversation below.</p>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} className={`lease-chat-message ${msg.user_type}`}>
                    <p>{msg.comment_text}</p>
                    <span>{msg.user_type === 'landlord' ? 'Owner' : 'Tenant'}</span>
                  </div>
                ))
              )}
            </div>
            <div className="lease-chat-compose">
              <textarea
                className="comment-box"
                rows="3"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder="Write a message to all active tenants..."
              />
              <button className="primary-button" onClick={handleSendChat} disabled={chatSending || !chatText.trim()}>
                {chatSending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>

          {/* Lease Renewal Section */}
          <div className="dashboard-section" style={{ marginTop: '1rem' }}>
            <div className="section-header" style={{ alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Lease Renewal</h3>
              {renewal && <span className={`status-pill ${renewal.status}`}>{renewal.status}</span>}
            </div>

            {renewalMessage && (
              <div style={{ padding: '0.5rem 0.75rem', borderLeft: '3px solid #059669', background: '#f0fff4', marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                <p style={{ margin: 0 }}>{renewalMessage}</p>
              </div>
            )}

            {renewal ? (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <p className="status-text"><strong>Proposed rent:</strong> {formatCurrency(renewal.proposed_rent)}/mo</p>
                  <p className="status-text"><strong>Start:</strong> {renewal.proposed_start_date || '—'}</p>
                  <p className="status-text"><strong>End:</strong> {renewal.proposed_end_date || '—'}</p>
                </div>
                {renewal.note && <p className="status-text" style={{ marginTop: '0.25rem' }}><strong>Note:</strong> {renewal.note}</p>}
                {renewal.counter_rent && (
                  <div style={{ background: '#fffbeb', padding: '0.5rem', borderRadius: '6px', marginTop: '0.5rem' }}>
                    <p className="status-text"><strong>Tenant counter:</strong> {formatCurrency(renewal.counter_rent)}/mo</p>
                    {renewal.counter_note && <p className="status-text">{renewal.counter_note}</p>}
                  </div>
                )}
                <div className="form-actions" style={{ marginTop: '0.75rem', gap: '0.5rem' }}>
                  {renewal.status === 'draft' && (
                    <button className="primary-button" onClick={async () => {
                      setRenewalLoading(true);
                      try {
                        const r = await fetch(`${API_BASE_URL}/api/landlords/lease-renewals/${renewal.id}/send`, {
                          method: 'POST', headers: { Authorization: `Bearer ${user.token}` }
                        });
                        if (r.ok) { const d = await r.json(); setRenewal(d.renewal); setRenewalMessage('Renewal sent to tenant!'); }
                        else { const e = await r.json().catch(() => ({})); setRenewalMessage(e.error || 'Failed to send'); }
                      } finally { setRenewalLoading(false); }
                    }} disabled={renewalLoading}>Send to Tenant</button>
                  )}
                  {(renewal.status === 'accepted' || renewal.status === 'countered') && (
                    <button className="primary-button" onClick={async () => {
                      if (!window.confirm('Activate this renewal? This will update the lease terms.')) return;
                      setRenewalLoading(true);
                      try {
                        const r = await fetch(`${API_BASE_URL}/api/landlords/lease-renewals/${renewal.id}/activate`, {
                          method: 'POST', headers: { Authorization: `Bearer ${user.token}` }
                        });
                        if (r.ok) { const d = await r.json(); setRenewal(d.renewal); setRenewalMessage('Renewal activated! Lease terms updated.'); }
                        else { const e = await r.json().catch(() => ({})); setRenewalMessage(e.error || 'Failed to activate'); }
                      } finally { setRenewalLoading(false); }
                    }} disabled={renewalLoading}>Activate Renewal</button>
                  )}
                  {['draft', 'sent', 'countered'].includes(renewal.status) && (
                    <button className="secondary-button" onClick={async () => {
                      if (!window.confirm('Cancel this renewal offer?')) return;
                      setRenewalLoading(true);
                      try {
                        const r = await fetch(`${API_BASE_URL}/api/landlords/lease-renewals/${renewal.id}/cancel`, {
                          method: 'POST', headers: { Authorization: `Bearer ${user.token}` }
                        });
                        if (r.ok) { setRenewal(null); setRenewalMessage('Renewal cancelled.'); }
                        else { const e = await r.json().catch(() => ({})); setRenewalMessage(e.error || 'Failed to cancel'); }
                      } finally { setRenewalLoading(false); }
                    }} disabled={renewalLoading} style={{ color: '#ef4444' }}>Cancel</button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {!showRenewalForm ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p className="status-text">No active renewal. Create one to offer new lease terms.</p>
                    <button className="secondary-button" onClick={() => {
                      const endDate = tenant?.lease_end_date;
                      const nextStart = endDate ? new Date(new Date(endDate).getTime() + 86400000).toISOString().slice(0, 10) : '';
                      const nextEnd = nextStart ? new Date(new Date(nextStart).getTime() + 365 * 86400000).toISOString().slice(0, 10) : '';
                      setRenewalForm(prev => ({
                        proposed_rent: prev.proposed_rent || tenant?.monthly_rent || '',
                        proposed_start_date: nextStart,
                        proposed_end_date: nextEnd,
                        note: ''
                      }));
                      setShowRenewalForm(true);
                    }} style={{ marginTop: '0.5rem' }}>Create Renewal Offer</button>
                  </div>
                ) : (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                      <div style={{ flex: '1 1 140px' }}>
                        <label className="status-text" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.2rem' }}>Rent (CAD/month)</label>
                        <input type="number" className="form-control" value={renewalForm.proposed_rent}
                          onChange={e => setRenewalForm(prev => ({ ...prev, proposed_rent: e.target.value }))} min="0" />
                      </div>
                      <div style={{ flex: '1 1 140px' }}>
                        <label className="status-text" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.2rem' }}>Start date</label>
                        <input type="date" className="form-control" value={renewalForm.proposed_start_date}
                          onChange={e => setRenewalForm(prev => ({ ...prev, proposed_start_date: e.target.value }))} />
                      </div>
                      <div style={{ flex: '1 1 140px' }}>
                        <label className="status-text" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.2rem' }}>End date</label>
                        <input type="date" className="form-control" value={renewalForm.proposed_end_date}
                          onChange={e => setRenewalForm(prev => ({ ...prev, proposed_end_date: e.target.value }))} />
                      </div>
                    </div>
                    <label className="status-text" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.2rem' }}>Note to tenant (optional)</label>
                    <textarea className="form-control" rows={2} value={renewalForm.note}
                      onChange={e => setRenewalForm(prev => ({ ...prev, note: e.target.value }))}
                      placeholder="e.g. Slight increase due to rising costs…" style={{ width: '100%', resize: 'vertical' }} />
                    <div className="form-actions" style={{ marginTop: '0.5rem', gap: '0.5rem' }}>
                      <button className="primary-button" onClick={async () => {
                        if (!renewalForm.proposed_rent || !renewalForm.proposed_start_date) {
                          setRenewalMessage('Rent and start date are required.'); return;
                        }
                        setRenewalLoading(true);
                        try {
                          const r = await fetch(`${API_BASE_URL}/api/landlords/active-tenants/${id}/renewal`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
                            body: JSON.stringify(renewalForm)
                          });
                          const d = await r.json().catch(() => ({}));
                          if (r.ok) {
                            setRenewal(d.renewal);
                            setShowRenewalForm(false);
                            setRenewalMessage('Renewal created as draft. Send it when ready.');
                          } else {
                            setRenewalMessage(d.error || 'Failed to create renewal');
                          }
                        } finally { setRenewalLoading(false); }
                      }} disabled={renewalLoading}>{renewalLoading ? 'Creating…' : 'Create Draft'}</button>
                      <button className="secondary-button" onClick={() => setShowRenewalForm(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="tenant-detail-actions">
            <button className="secondary-button" onClick={handleEndTenancy} disabled={endingTenancy || tenant.status === 'ended'}>
              {tenant.status === 'ended' ? 'Tenancy ended' : endingTenancy ? 'Ending tenancy…' : 'End Tenancy'}
            </button>
          </div>
        </article>
      </section>
    </main>
  );
}

export default MyTenantDetail;
